import { Fragment, useEffect, useRef, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PhoneIcon,
  UserPlusIcon,
} from "lucide-react";

import { KanbanCandidacyCard } from "./KanbanCandidacyCard";
import { AddCandidateDialog } from "../AddCandidateDialog";
import { Center, Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

import {
  candidacyStatusToLabel,
  KANBAN_COLUMN_WIDTH_CLASS,
  pipelineStageToDefaultCandidacyStatus,
  pipelineStageToKanbanDescription,
  pipelineStageToKanbanEmptyState,
  pipelineStageToKanbanToneClass,
  pipelineStageToLabel,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { stevieRoute } from "@/lib/stevieRoute";
import { cn } from "@/lib/utils";
import {
  CandidacyStatus,
  MinimalCandidacy,
  PipelineStage,
  PipelineViewPreference,
} from "@/types";

// Pointer travel before a press turns into a drag instead of a card click.
const DRAG_ACTIVATION_DISTANCE_PX = 5;

export const JobPipelineKanban = ({
  onCandidacyClick,
  onMoveCandidacyToStage,
  onOpenMessages,
  onRequestStatusChangeDialog,
  onReviewAllApplied,
  setAppliedRegionRef,
  stageGroups,
}: {
  onCandidacyClick: (candidacy: MinimalCandidacy) => void;
  onMoveCandidacyToStage: (
    candidacy: MinimalCandidacy,
    stage: PipelineStage,
  ) => void;
  onOpenMessages: (candidacy: MinimalCandidacy) => void;
  onRequestStatusChangeDialog: (
    candidacyId: string,
    status: CandidacyStatus,
  ) => void;
  onReviewAllApplied: (appliedCandidacyIds: string[]) => void;
  setAppliedRegionRef: (el: HTMLElement | null) => void;
  stageGroups: { stage: PipelineStage; rows: MinimalCandidacy[] }[];
}) => {
  /* React Hooks */
  // useState
  const [addCandidateStage, setAddCandidateStage] =
    useState<PipelineStage | null>(null);
  // The card currently being dragged, plus the stage it started in.
  const [activeDrag, setActiveDrag] = useState<{
    candidacy: MinimalCandidacy;
    stage: PipelineStage;
  } | null>(null);
  // Tracks whether the kanban scroll container has room to scroll in each direction.
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // useSensors — a small activation distance keeps plain clicks opening the sheet.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    }),
  );

  // useRef — points at the horizontally scrollable Row that wraps the stage columns.
  const kanbanScrollRef = useRef<HTMLDivElement>(null);

  // useEffect — keep arrow visibility in sync with the live scroll position and viewport size.
  useEffect(() => {
    const el = kanbanScrollRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 4);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [stageGroups]);

  /* Handlers */
  const handleAddCandidate = (stage: PipelineStage) => {
    mixpanelUtils.trackButtonClick("Add candidate", MIXPANEL_SCREENS.JOB, {
      pipeline_view: PipelineViewPreference.KANBAN,
      stage,
    });
    setAddCandidateStage(stage);
  };

  // Scrolls the kanban one viewport width left (-1) or right (1).
  const scrollKanban = (direction: 1 | -1) => {
    const el = kanbanScrollRef.current;
    if (!el) return;
    mixpanelUtils.trackButtonClick("Kanban scroll", MIXPANEL_SCREENS.JOB, {
      pipeline_view: PipelineViewPreference.KANBAN,
      direction: direction === 1 ? "right" : "left",
    });
    el.scrollBy({ left: direction * el.clientWidth, behavior: "smooth" });
  };

  const handleCardClick = (candidacy: MinimalCandidacy) => {
    onCandidacyClick(candidacy);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const dragged = event.active.data.current as
      | { candidacy: MinimalCandidacy; stage: PipelineStage }
      | undefined;
    if (dragged) setActiveDrag(dragged);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDrag(null);
    const dragged = event.active.data.current as
      | { candidacy: MinimalCandidacy; stage: PipelineStage }
      | undefined;
    const targetStage = event.over?.data.current?.stage as
      | PipelineStage
      | undefined;
    if (!dragged || !targetStage) return;
    onMoveCandidacyToStage(dragged.candidacy, targetStage);
  };

  const handleDragCancel = () => {
    setActiveDrag(null);
  };

  /* Render */
  const render = () => (
    <DndContext
      collisionDetection={pointerWithin}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      sensors={sensors}
    >
      {/* Outer Row anchors the absolute-positioned scroll arrows; overflow-hidden keeps it from growing past the page width. */}
      <Row className="relative h-full w-full min-w-0 items-stretch overflow-hidden">
        <Row
          ref={kanbanScrollRef}
          className="h-full min-w-0 flex-1 items-stretch gap-2 overflow-x-auto pb-2"
        >
          {stageGroups.map(({ stage, rows }) => (
            <Fragment key={stage}>
              {stage === PipelineStage.TO_REVIEW ? <ScreeningDivider /> : null}
              <KanbanStageColumn
                activeDragStage={activeDrag?.stage ?? null}
                columnRef={(el) => {
                  if (stage === PipelineStage.APPLIED) {
                    setAppliedRegionRef(el);
                  }
                }}
                onAddCandidate={() => {
                  handleAddCandidate(stage);
                }}
                onCardClick={handleCardClick}
                onOpenMessages={onOpenMessages}
                onRequestStatusChangeDialog={onRequestStatusChangeDialog}
                onReviewAllApplied={() => {
                  // Candidacies waiting on a Stevie call need no decision yet.
                  onReviewAllApplied(
                    rows
                      .filter((row) => !stevieRoute.isAwaitingCall(row))
                      .map((row) => row.id),
                  );
                }}
                rows={rows}
                stage={stage}
              />
            </Fragment>
          ))}
        </Row>
        {/* Arrow buttons only render when there's room to scroll in that direction. */}
        {canScrollLeft ? (
          <Button
            className="absolute left-1 top-1/2 z-10 h-9 w-9 -translate-y-1/2 rounded-full border-slate-200 bg-white text-slate-700 shadow-md hover:bg-slate-50"
            onClick={() => {
              scrollKanban(-1);
            }}
            size="icon"
            variant="outline"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </Button>
        ) : null}
        {canScrollRight ? (
          <Button
            className="absolute right-1 top-1/2 z-10 h-9 w-9 -translate-y-1/2 rounded-full border-slate-200 bg-white text-slate-700 shadow-md hover:bg-slate-50"
            onClick={() => {
              scrollKanban(1);
            }}
            size="icon"
            variant="outline"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </Button>
        ) : null}
        <AddCandidateDialog
          defaultStatus={
            addCandidateStage
              ? pipelineStageToDefaultCandidacyStatus[addCandidateStage]
              : undefined
          }
          onOpenChange={(next) => {
            if (!next) setAddCandidateStage(null);
          }}
          open={addCandidateStage !== null}
        />
      </Row>
      <DragOverlay dropAnimation={null}>
        {activeDrag ? (
          <KanbanCandidacyCard
            candidacy={activeDrag.candidacy}
            isDragOverlay
            onClick={() => undefined}
            onOpenMessages={() => undefined}
            onRequestStatusChangeDialog={() => undefined}
            stage={activeDrag.stage}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );

  return render();
};

const KanbanStageColumn = ({
  activeDragStage,
  columnRef,
  stage,
  rows,
  onAddCandidate,
  onCardClick,
  onOpenMessages,
  onRequestStatusChangeDialog,
  onReviewAllApplied,
}: {
  // The stage the in-flight card came from, or null when nothing is dragging.
  activeDragStage: PipelineStage | null;
  columnRef: (el: HTMLDivElement | null) => void;
  stage: PipelineStage;
  rows: MinimalCandidacy[];
  onAddCandidate: () => void;
  onCardClick: (candidacy: MinimalCandidacy) => void;
  onOpenMessages: (candidacy: MinimalCandidacy) => void;
  onRequestStatusChangeDialog: (
    candidacyId: string,
    status: CandidacyStatus,
  ) => void;
  onReviewAllApplied: () => void;
}) => {
  // Any bucket but the card's own is a valid target, in either direction.
  const isDropTarget = activeDragStage !== null && activeDragStage !== stage;
  const { isOver, setNodeRef } = useDroppable({
    data: { stage },
    disabled: !isDropTarget,
    id: stage,
  });

  // Hovering a valid target hides the cards behind the status the drop lands on.
  const isDropHovered = isDropTarget && isOver;
  const dropStatusLabel =
    candidacyStatusToLabel[pipelineStageToDefaultCandidacyStatus[stage]];
  // Bulk review skips candidacies waiting on a Stevie call.
  const reviewableCount = rows.filter(
    (row) => !stevieRoute.isAwaitingCall(row),
  ).length;

  /* Render */
  const render = () => (
    <Column
      ref={(el) => {
        columnRef(el);
        setNodeRef(el);
      }}
      className={cn(
        "h-full shrink-0 rounded-xl transition-shadow",
        KANBAN_COLUMN_WIDTH_CLASS,
        pipelineStageToKanbanToneClass[stage],
        isDropHovered && "ring-2 ring-inset ring-emerald-500",
      )}
    >
      <Column className="px-3 pb-2 pt-3">
        <Row className="items-center justify-between gap-2">
          <Row className="min-w-0 items-center gap-1.5">
            <Label className="truncate text-base font-semibold text-slate-900">
              {pipelineStageToLabel[stage]}
            </Label>
            <Text className="text-base font-normal text-slate-500">·</Text>
            <Text className="text-base font-medium tabular-nums text-slate-700">
              {rows.length.toLocaleString()}
            </Text>
          </Row>
          <Button
            className="h-7 w-7 shrink-0 text-slate-600 hover:bg-slate-200/80 hover:text-slate-900"
            onClick={onAddCandidate}
            variant="ghost"
          >
            <UserPlusIcon />
          </Button>
        </Row>
        <Text className="mt-1 text-xs font-normal leading-snug text-slate-700">
          {pipelineStageToKanbanDescription[stage]}
        </Text>
        {stage === PipelineStage.APPLIED && rows.length > 0 ? (
          <Card className="mt-2 border-slate-200 bg-white p-3">
            <Column className="gap-3">
              <Column className="gap-1">
                <Label className="text-sm font-semibold text-slate-900">
                  Review applications in bulk
                </Label>
                <Text className="text-xs font-normal leading-snug text-slate-500">
                  Walk through all new applicants in a focused queue to quickly
                  accept or reject.
                </Text>
              </Column>
              <Button
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  onReviewAllApplied();
                }}
              >
                Review all ({reviewableCount.toLocaleString()})
              </Button>
            </Column>
          </Card>
        ) : null}
      </Column>
      <Column className="relative min-h-0 flex-1">
        <Column
          className={cn(
            "min-h-0 flex-1 gap-1.5 overflow-y-auto px-3 pb-10 pt-1",
            isDropHovered && "pointer-events-none opacity-20 blur-[2px]",
          )}
        >
          <KanbanCards
            isAnyDragging={activeDragStage !== null}
            onCardClick={onCardClick}
            onOpenMessages={onOpenMessages}
            onRequestStatusChangeDialog={onRequestStatusChangeDialog}
            rows={rows}
            stage={stage}
          />
        </Column>
        {isDropHovered ? (
          <Center className="pointer-events-none absolute inset-0 px-4">
            <Text className="text-center text-sm font-semibold text-slate-800">
              Move candidacy to {dropStatusLabel}
            </Text>
          </Center>
        ) : null}
      </Column>
    </Column>
  );

  return render();
};

const KanbanCards = ({
  isAnyDragging,
  onCardClick,
  onOpenMessages,
  onRequestStatusChangeDialog,
  rows,
  stage,
}: {
  isAnyDragging: boolean;
  onCardClick: (candidacy: MinimalCandidacy) => void;
  onOpenMessages: (candidacy: MinimalCandidacy) => void;
  onRequestStatusChangeDialog: (
    candidacyId: string,
    status: CandidacyStatus,
  ) => void;
  rows: MinimalCandidacy[];
  stage: PipelineStage;
}) => {
  const renderCard = (candidacy: MinimalCandidacy) => (
    <KanbanCandidacyCard
      candidacy={candidacy}
      isAnyDragging={isAnyDragging}
      key={candidacy.id}
      onClick={onCardClick}
      onOpenMessages={onOpenMessages}
      onRequestStatusChangeDialog={onRequestStatusChangeDialog}
      stage={stage}
    />
  );

  /* Render */
  const render = () => {
    // Empty State
    if (rows.length === 0) {
      return (
        <Text className="text-xs font-normal italic text-slate-600">
          {pipelineStageToKanbanEmptyState[stage]}
        </Text>
      );
    }

    // Candidacies waiting on a Stevie call drop below the ones still needing a
    // decision — they're already handled until the call lands.
    if (stage === PipelineStage.APPLIED) {
      const awaitingStevie = rows.filter(stevieRoute.isAwaitingCall);
      if (awaitingStevie.length > 0) {
        const toReview = rows.filter((c) => !stevieRoute.isAwaitingCall(c));
        return (
          <>
            {toReview.map((c) => renderCard(c))}
            <KanbanSectionHeader
              count={awaitingStevie.length}
              icon={<PhoneIcon className="h-3.5 w-3.5 text-slate-500" />}
              label="Sent to Stevie"
            />
            {awaitingStevie.map((c) => renderCard(c))}
          </>
        );
      }
    }

    return <>{rows.map((c) => renderCard(c))}</>;
  };

  return render();
};

const KanbanSectionHeader = ({
  count,
  icon,
  label,
}: {
  count: number;
  icon: React.ReactNode;
  label: string;
}) => (
  <Row className="items-center gap-1.5 pt-1">
    {icon}
    <Text className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
      {label} <span className="font-normal tabular-nums">({count})</span>
    </Text>
  </Row>
);

const ScreeningDivider = () => (
  <Column className="relative w-6 shrink-0 self-stretch">
    <span
      aria-hidden
      className="absolute inset-y-2 left-1/2 w-px -translate-x-1/2 border-l border-dashed border-slate-300"
    />
    <Row className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-90 whitespace-nowrap rounded-full bg-white px-1.5 py-[1px] ring-1 ring-inset ring-zinc-300">
      <Text className="text-[10px] font-medium leading-tight text-zinc-700">
        Screening
      </Text>
    </Row>
  </Column>
);
